import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect, useRef, useState} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import InformationBox from '../../../../../components/other/informationBox';
import PropTypes from 'prop-types';
import {emitChangedWait, getObsScore, obsStatuses, obsTypes} from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import {useTheme} from '@mui/material/styles';
import {FormLabel} from '@mui/material';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import {obsStyles} from '../obsStyle';

const venturiOptions = [
    {value: 24, color: '#c9daf8', label: 'blue', description: '24%', activeColor: '#3C78D8'},
    {value: 28, color: '#efefef', label: 'gray', description: '28%', activeColor: '#999999'},
    {value: 31, color: '#fce5cd', label: 'orange', description: '31%', activeColor: '#E69138'},
    {value: 35, color: '#fff2cc', label: 'yellow', description: '35%', activeColor: '#f1c232'},
    {value: 40, color: '#e6b8af', label: 'red', description: '40%', activeColor: '#e86e56'},
    {value: 60, color: '#d9ead3', label: 'green', description: '60%', activeColor: '#6AA84F'},
];

function ObsAirOxygen(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [airType, setAirType] = React.useState(allowDefaultValue ? props.airType : null);
    const [device, setDevice] = React.useState(allowDefaultValue ? props.device : null);
    const [inspiredOxygen, setInspiredOxygen] = React.useState(props.inspiredOxygen);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);
    const deviceChange = useRef(false);

    const [inspiredOxygenRange, setInspiredOxygenRange] = React.useState(type === obsTypes.news
        ? {min: 22, max: 100, default: 22}
        : {min: 0, max: 100, default: 0, warning: 2, unit: 'L/min'},
    );
    const [litres, setLitres] = React.useState(props.litter);

    const [oxygenDevices, setOxygenDevices] = React.useState([]);

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setAirType(props.airType);
            setDevice(props.device);
            setInspiredOxygenRange(props.inspiredOxygenRange);
            setInspiredOxygen(props.inspiredOxygen);
            setLitres(props.litres);
        }
    }, []);

    // Init devices
    useState(() => {
        const devices = [];
        if (type === obsTypes.news) {
            devices.push({
                id: 'headBox',
                description: 'Head Box',
                min: 22,
                max: 100,
                default: 22,
                defaultDevice: true,
            });
            devices.push({
                id: 'highFlow',
                description: 'High Flow',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'optiflow',
                description: 'Optiflow',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'nasalCannula',
                description: 'Nasal Cannula',
            });
            devices.push({
                id: 'simpleMask',
                description: 'Simple Mask',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'nonRebreatheMask',
                description: 'With resevoir Non-rebreath',
                min: 22,
                max: 100,
                default: 85,
            });
            devices.push({
                id: 'humidifiedSystem',
                description: 'Humidified system',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'venturi',
                description: 'Venturi mask',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'tracheostomy',
                description: 'Tracheostomy',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'aerosalNeb',
                description: 'Aerosal / neb',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'intubated',
                description: 'Intubated',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'nivBiPAP',
                description: 'NIV BiPAP',
                min: 22,
                max: 100,
                default: 22,
            });
            devices.push({
                id: 'nivCPAP',
                description: 'NIV CPAP',
                min: 22,
                max: 100,
                default: 22,
            });
        } else {
            devices.push({
                id: 'np',
                description: 'Nasal Prongs',
                min: 0.01,
                max: 100,
                warning: 2,
                default: 0.01,
                unit: 'L/min',
                defaultDevice: true,
            });
            devices.push({
                id: 'fm',
                description: 'Face Mask',
                min: 0.01,
                max: 100,
                warning: 10,
                unit: 'L/min',
                default: 0.01,
            });
            devices.push({
                id: 'hb',
                description: 'Head Box',
                min: 0.01,
                max: 100,
                warning: 20,
                unit: 'L/min',
                default: 0.01,
            });
            devices.push({
                id: 'nrb',
                description: 'Non-re-breathe',
                min: 10,
                max: 100,
                warning: 15,
                unit: 'L/min',
                default: 10,
            });
            devices.push({
                id: 'hfno',
                description: 'High flow Nasal O2',
                min: 0.01,
                max: 100,
                warning: 65,
                unit: 'L/min',
                default: 0.01,
            });
            devices.push({
                id: 'nivBiPAP',
                description: 'NIV BiPAP',
                min: 0.01,
                max: 100,
                warning: 20,
                unit: 'L/min',
                default: 0.01,
            });
            devices.push({
                id: 'nivCPAP',
                description: 'NIV CPAP',
                min: 0.01,
                max: 100,
                warning: 20,
                unit: 'L/min',
                default: 0.01,
            });
        }
        setOxygenDevices(devices);
    }, [type]);

    function setDefaults() {
        setAirType(allowDefaultValue ? 'room' : null);
        setInspiredOxygen(undefined);
        setDevice(allowDefaultValue ? props.device : null);
    }

    useEffect(() => {
        if ((_.isUndefined(airType) || _.isNull(airType)) && allowDefaultValue) {
            if (_.isUndefined(props.airType) || props.airType == null)
                setDefaults();
            else
                setAirType(props.airType);
        }
    }, [props.airType]);

    const clearAccepted = (formAcceptSection) => {
        if (!allowDefaultValue && formAcceptSection) {
            setScore(0);
            setAirType(null);
        }

        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) setStatus(obsStatuses.completed);
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(airType) && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) acceptObs();
    }, [airType, device, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    airType,
                    device,
                    inspiredOxygen,
                    score,
                    accepted,
                    acceptDate,
                    inspiredOxygenRange,
                    litres,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        status,
        airType,
        device,
        inspiredOxygen,
        score,
        accepted,
        acceptDate,
        inspiredOxygenRange,
        litres,
    ]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (type === obsTypes.pews && airType === 'oxygen') {
                //newScore = inspiredOxygen >= 4 ? 4 : 2;
                newScore = getObsScore('oxygen', type, ageRangeYears, inspiredOxygen);
            } else if (type === obsTypes.news && airType === 'oxygen') {
                newScore = 2;
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, airType, inspiredOxygen]);

    const onInspiredOxygenChange = (value) => {
        setInspiredOxygen(value);
        clearAccepted();
    };

    const updateAirType = (newAirType) => {
        setAirType(newAirType);
        clearAccepted();
    };

    const updateDevice = (newDevice) => {
        setDevice(newDevice);
        deviceChange.current = true;
        clearAccepted();
    };

    const onLitresChange = (newLitres) => {
        setLitres(newLitres);
        clearAccepted();
    };

    useEffect(() => {
        if (airType === 'room') {
            setDevice(null);
        } else {
            if (!device && allowDefaultValue) {
                let def = _.find(oxygenDevices, (item) => !!item.defaultDevice);
                if (def) setDevice(def);
            }
        }
    }, [airType]);

    // Default values for device
    useEffect(() => {
        if (device) {
            if (device.id === 'nasalCannula') {
                if ((!props.ipodView && props.status !== obsStatuses.new) || deviceChange.current) {
                    setLitres(allowDefaultValue ? 1 : undefined);
                }
            } else {
                setInspiredOxygenRange({
                    min: device.min,
                    max: device.max,
                    warning: device?.warning,
                    unit: device?.unit,
                    default: allowDefaultValue ? device.default : undefined,
                });
                if ((!props.ipodView && props.status !== obsStatuses.new) || deviceChange.current) {
                    setInspiredOxygen(allowDefaultValue ? device.default : undefined);
                    setLitres(undefined);
                }
            }
            if (deviceChange.current) deviceChange.current = false;
        } else {
            if ((!props.ipodView && props.status !== obsStatuses.new)) {
                setLitres(undefined);
                setInspiredOxygen(undefined);
            }
        }
    }, [device]);

    useEffect(() => {
        if (device && device.id === 'nasalCannula') {
            let v = 24;
            if (litres === 1) {
                v = 24;
            } else if (litres === 2) {
                v = 28;
            } else if (litres === 3) {
                v = 32;
            } else if (litres === 4) {
                v = 36;
            } else if (litres === 5) {
                v = 40;
            }
            setInspiredOxygenRange({min: v, max: v, default: v});
            setInspiredOxygen(litres ? v : undefined);
        }
    }, [litres, device]);

    const onStatusChange = (value) => {
        resetObs();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container direction="row" alignItems={'center'} spacing={1}>
                    <Grid item xs={6}>
                        <AgyleBlockSwitch
                            onChange={(value) => updateAirType(value)}
                            value={airType}
                            customLabels={{
                                yes: t('Room Air'),
                                no: t('Oxygen'),
                            }}
                            valueObject={{
                                true: 'room',
                                false: 'oxygen',
                            }}
                            style={{
                                backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                            }}
                        />
                    </Grid>

                    {airType === 'oxygen' &&
                        <Grid item xs={12} className={classes.component}>
                            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                                <Grid item xs={12}>
                                    <FormLabel className={classes.label}>
                                        {t('Devices')}
                                    </FormLabel>
                                </Grid>

                                <Grid item xs={6} className={classes.deviceChips}>
                                    <AgyleDictionaryField
                                        onChange={(_, device) => updateDevice(device)}
                                        options={oxygenDevices}
                                        value={device}
                                        getOptionLabel={(option) => option.description}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        extended
                                        noBorder={false}
                                    />
                                </Grid>

                                <Grid item xs={6} style={{marginLeft: 'auto'}}>
                                    {device && device.id === 'nasalCannula' && (
                                        <ObsNumericStepper
                                            value={litres}
                                            minValue={1}
                                            maxValue={5}
                                            label={t('Litres')}
                                            onChange={onLitresChange}
                                            column
                                        />
                                    )}
                                    <ObsNumericStepper
                                        value={inspiredOxygen}
                                        minValue={inspiredOxygenRange.min}
                                        maxValue={inspiredOxygenRange.max}
                                        label={t(type === obsTypes.pews ? inspiredOxygenRange.unit : 'Inspired Oxygen')}
                                        step={type === obsTypes.pews ? 0.01 : 1}
                                        device={device}
                                        precision={type === obsTypes.pews ? 2 : 0}
                                        onChange={onInspiredOxygenChange}
                                        disabled={!device}
                                        column
                                    />
                                </Grid>

                                {type === obsTypes.pews &&
                                    inspiredOxygenRange &&
                                    inspiredOxygen > inspiredOxygenRange.warning &&
                                    device &&
                                    device.id !== 'nivCPAP' &&
                                    device.id !== 'nivBiPAP' &&
                                    <Grid item xs={12}>
                                        <InformationBox type={'warning'}>
                                            {t(
                                                'Warning – the value entered exceeds the recommended oxygen flow rate for this device.',
                                            )}
                                        </InformationBox>
                                    </Grid>
                                }

                                <Grid item xs={12}>
                                    {device && device.id === 'venturi' &&
                                        <div className={classes.divRow}>
                                            {venturiOptions.map((mask) => {
                                                return (
                                                    <AgyleButton
                                                        key={mask.value}
                                                        style={{
                                                            background: inspiredOxygen === mask.value ? mask.activeColor : mask.color,
                                                            color: 'black',
                                                        }}
                                                        onClick={() => onInspiredOxygenChange(mask.value)}
                                                        variant="outlined"
                                                    >
                                                        {mask.description}
                                                    </AgyleButton>
                                                );
                                            })}
                                        </div>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    }

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: type === obsTypes.neuro,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(airType),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsAirOxygen.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    airType: PropTypes.oneOf(['room', 'oxygen']),
    device: PropTypes.object,
    inspiredOxygen: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsAirOxygen.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsAirOxygen);